import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouterHooks } from "../../withRouters/withRoutersHook";
import {
  FetchAllDepartmentsAction,
  CreateDepartmentAction,
  UpdateDepartmentAction,
  DeleteDepartmentAction,
} from "../../Actions/AdminUser";
import { ChevronLeft, ChevronRight, Building2, Pencil, Trash2, EllipsisVertical } from "lucide-react";
import Spinner from "../Animations/Spinner/Spinner";
import Modal from "../Common/Modal";
import DropdownField from "../Dashboard/FileManager/DropdownField";

class CreateDepartments extends Component {
  state = {
    departmentName: "",
    departmentDescription: "",
    departments: [],
    isEditing: false,
    editingDepartment: null,
    showDeleteModal: false,
    deletingDepartment: null,
    isLoading: false,
    error: null,
    success: null,
    selectedParentId: null,
    showFormModal: false,
    isSubMode: false,
    parentPreselected: false,
    expandedDepartments: new Set(),
    deleteConfirmationName: "",
    openActionMenuId: null,
    actionMenuPosition: null,
  };

  static propTypes = {
    FetchAllDepartmentsAction: PropTypes.func.isRequired,
    CreateDepartmentAction: PropTypes.func.isRequired,
    UpdateDepartmentAction: PropTypes.func.isRequired,
    DeleteDepartmentAction: PropTypes.func.isRequired,
    departments: PropTypes.array,
  };

  componentDidMount() {
    this.fetchDepartments();
    document.addEventListener("mousedown", this.handleClickOutsideMenu);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutsideMenu);
  }

  handleClickOutsideMenu = () => {
    if (this.state.openActionMenuId !== null) {
      this.setState({ openActionMenuId: null, actionMenuPosition: null });
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.departments !== this.props.departments) {
      this.setState({ departments: this.props.departments || [] });
    }
  }

  fetchDepartments = async () => {
    this.setState({ isLoading: true, error: null });
    try {
      const result = await this.props.FetchAllDepartmentsAction();
      this.setState({ departments: result?.departments || [], isLoading: false });
    } catch (error) {
      this.setState({ error: "Failed to fetch departments", isLoading: false });
    }
  };

  handleInputChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleParentChange = (e) => {
    const val = e.target.value;
    this.setState({ selectedParentId: !val ? null : Number(val) });
  };

  handleCreateDepartment = async () => {
    const { departmentName, departmentDescription } = this.state;
    if (!departmentName.trim()) { this.setState({ error: "Department name is required" }); return; }
    if (!departmentDescription.trim()) { this.setState({ error: "Department description is required" }); return; }
    this.setState({ isLoading: true, error: null });
    try {
      await this.props.CreateDepartmentAction({ name: departmentName, description: departmentDescription });
      this.closeModal();
      this.setState({ success: "Department created successfully", isLoading: false });
      setTimeout(() => this.setState({ success: null }), 3000);
      this.fetchDepartments();
    } catch (error) {
      this.setState({ error: "Failed to create department", isLoading: false });
    }
  };

  handleCreateSubDepartment = async () => {
    const { departmentName, departmentDescription, selectedParentId } = this.state;
    if (!departmentName.trim()) { this.setState({ error: "Sub-department name is required" }); return; }
    if (!departmentDescription.trim()) { this.setState({ error: "Sub-department description is required" }); return; }
    if (!selectedParentId) { this.setState({ error: "Parent department is required" }); return; }
    this.setState({ isLoading: true, error: null });
    try {
      await this.props.CreateDepartmentAction({ name: departmentName, description: departmentDescription, parent_id: selectedParentId });
      this.closeModal();
      this.setState({ success: "Sub-department created successfully", isLoading: false });
      setTimeout(() => this.setState({ success: null }), 3000);
      this.fetchDepartments();
    } catch (error) {
      this.setState({ error: "Failed to create sub-department", isLoading: false });
    }
  };

  handleEditClick = (department) => {
    this.setState({ isEditing: true, editingDepartment: department, departmentName: department.name, departmentDescription: department.description || "", showFormModal: true, isSubMode: false, error: null });
  };

  handleUpdateDepartment = async () => {
    const { editingDepartment, departmentName, departmentDescription } = this.state;
    if (!departmentName.trim()) { this.setState({ error: "Department name cannot be empty" }); return; }
    if (!departmentDescription.trim()) { this.setState({ error: "Department description cannot be empty" }); return; }
    this.setState({ isLoading: true, error: null });
    try {
      await this.props.UpdateDepartmentAction(editingDepartment.id, { name: departmentName, description: departmentDescription });
      await this.fetchDepartments();
      this.closeModal();
      this.setState({ success: "Department updated successfully", isLoading: false });
      setTimeout(() => this.setState({ success: null }), 3000);
    } catch (error) {
      this.setState({ error: "Failed to update department", isLoading: false });
    }
  };

  handleDeleteClick = (department) => {
    this.setState({ showDeleteModal: true, deletingDepartment: department, deleteConfirmationName: "", error: null });
  };

  handleConfirmDelete = async () => {
    const { deletingDepartment, deleteConfirmationName } = this.state;
    if (deleteConfirmationName !== deletingDepartment.name) {
      this.setState({ error: "The department name entered does not match. Please try again." });
      return;
    }
    this.setState({ isLoading: true, error: null });
    try {
      await this.props.DeleteDepartmentAction(deletingDepartment.id);
      await this.fetchDepartments();
      this.setState({ showDeleteModal: false, deletingDepartment: null, deleteConfirmationName: "", success: "Department deleted successfully", isLoading: false });
      setTimeout(() => this.setState({ success: null }), 3000);
    } catch (error) {
      this.setState({ error: "Failed to delete department", isLoading: false });
    }
  };

  handleDeleteConfirmationNameChange = (e) => {
    this.setState({ deleteConfirmationName: e.target.value, error: null });
  };

  toggleDepartmentExpand = (departmentId) => {
    this.setState((prevState) => {
      const newExpanded = new Set(prevState.expandedDepartments);
      if (newExpanded.has(departmentId)) { newExpanded.delete(departmentId); } else { newExpanded.add(departmentId); }
      return { expandedDepartments: newExpanded };
    });
  };

  handleBackToSettings = () => {
    this.props.navigate("/admin-dashboard");
  };

  closeModal = () => {
    this.setState({ showFormModal: false, isSubMode: false, isEditing: false, editingDepartment: null, departmentName: "", departmentDescription: "", selectedParentId: null, error: null, parentPreselected: false });
  };

  renderDepartmentItem = (department, isSubDepartment = false) => {
    const { expandedDepartments, openActionMenuId } = this.state;
    const hasSubDepartments = Array.isArray(department.sub_departments) && department.sub_departments.length > 0;
    const isExpanded = expandedDepartments.has(department.id);
    const isMenuOpen = openActionMenuId === department.id;

    return (
      <div key={department.id} className={isSubDepartment ? "bg-gray-50/30" : ""}>
        <div className="grid grid-cols-[1fr_5rem] items-center px-6 py-3 hover:bg-gray-50 transition-colors">
          {/* Department column */}
          <div className="flex items-center gap-2 min-w-0" style={isSubDepartment ? { paddingLeft: "2.75rem" } : {}}>
            {hasSubDepartments && (
              <button
                onClick={() => this.toggleDepartmentExpand(department.id)}
                className="p-0.5 rounded-md hover:bg-gray-100 transition-colors text-gray-400 flex-shrink-0"
              >
                <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`} />
              </button>
            )}
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isSubDepartment ? "bg-gray-100" : "bg-primary-blue/10"}`}>
              <Building2 className={`w-3.5 h-3.5 ${isSubDepartment ? "text-gray-500" : "text-primary-blue"}`} />
            </div>
            <div className="min-w-0">
              <p className={`text-sm font-medium truncate ${isSubDepartment ? "text-gray-600" : "text-gray-900"}`}>{department.name}</p>
              {department.description && (
                <p className="text-xs text-gray-400 mt-0.5 truncate">{department.description}</p>
              )}
            </div>
          </div>

          {/* Actions column */}
          <div className="flex justify-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isMenuOpen) {
                  this.setState({ openActionMenuId: null, actionMenuPosition: null });
                } else {
                  const r = e.currentTarget.getBoundingClientRect();
                  this.setState({ openActionMenuId: department.id, actionMenuPosition: { top: r.bottom + 4, right: window.innerWidth - r.right } });
                }
              }}
              className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              aria-label="Actions"
            >
              <EllipsisVertical className="w-4 h-4" />
            </button>
          </div>
        </div>
        {hasSubDepartments && isExpanded && (
          <div className="border-t border-gray-100">
            {department.sub_departments.map((subDept) => (
              <React.Fragment key={subDept.id}>
                {this.renderDepartmentItem(subDept, true)}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  };

  renderFormModal = () => {
    const { isEditing, isSubMode, parentPreselected, departmentName, departmentDescription, selectedParentId, isLoading, departments, error } = this.state;

    const title = isEditing ? "Edit Department" : "New Department";
    const handleSubmit = isEditing ? this.handleUpdateDepartment : isSubMode ? this.handleCreateSubDepartment : this.handleCreateDepartment;
    const submitLabel = isEditing ? "Save Changes" : isSubMode ? "Create Sub-Department" : "Create Department";

    return (
      <Modal title={title} onClose={this.closeModal}>
        <div className="flex flex-col gap-4">
          {/* Tab toggle — only shown when creating and parent not pre-selected */}
          {!isEditing && !parentPreselected && (
            <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg w-fit">
              <button
                onClick={() => this.setState({ isSubMode: false, selectedParentId: null, error: null })}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${!isSubMode ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              >
                Department
              </button>
              <button
                onClick={() => this.setState({ isSubMode: true, error: null })}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${isSubMode ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              >
                Sub-Department
              </button>
            </div>
          )}

          {/* Parent selector — only shown when in sub mode and parent not pre-selected */}
          {isSubMode && !parentPreselected && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Parent Department</label>
              <DropdownField
                name="selectedParentId"
                value={selectedParentId || ""}
                onChange={this.handleParentChange}
                placeholder="Select parent department"
                options={Array.isArray(departments) ? departments.map((dept) => ({ value: dept.id, label: dept.name })) : []}
              />
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">
              {isSubMode ? "Sub-Department" : "Department"} Name
            </label>
            <input
              autoFocus
              type="text"
              name="departmentName"
              value={departmentName}
              onChange={this.handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 outline-none focus:border-primary-blue transition-colors"
              placeholder={`Enter ${isSubMode ? "sub-department" : "department"} name`}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">
              {isSubMode ? "Sub-Department" : "Department"} Description
            </label>
            <textarea
              name="departmentDescription"
              value={departmentDescription}
              onChange={this.handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 outline-none focus:border-primary-blue transition-colors resize-none"
              placeholder={`Enter ${isSubMode ? "sub-department" : "department"} description`}
            />
          </div>
          {error && (
            <p className="text-xs text-red-500">{error}</p>
          )}
          <div className="flex gap-3 pt-1">
            <button
              onClick={this.closeModal}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="px-5 py-2 bg-primary-blue text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {isLoading ? <><span>{isEditing ? "Saving..." : "Creating..."}</span><Spinner /></> : submitLabel}
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  // Find a department by id across nested sub_departments
  findDepartmentById = (departments, id) => {
    for (const dept of departments) {
      if (dept.id === id) return dept;
      if (dept.sub_departments) {
        const found = this.findDepartmentById(dept.sub_departments, id);
        if (found) return found;
      }
    }
    return null;
  };

  render() {
    const {
      success, showDeleteModal, deletingDepartment,
      showFormModal, departments, isEditing, isLoading,
      deleteConfirmationName, error, openActionMenuId, actionMenuPosition,
    } = this.state;

    const menuDepartment = openActionMenuId ? this.findDepartmentById(departments, openActionMenuId) : null;
    const menuDepartmentIsTop = openActionMenuId ? departments.some((d) => d.id === openActionMenuId) : false;

    return (
      <div className="flex flex-col w-full h-full px-4 md:px-8 pt-6 md:pt-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={this.handleBackToSettings}
            className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="font-SFPro font-bold text-2xl text-gray-900">Manage Departments</h1>
          <div className="ml-auto">
            <button
              onClick={() => this.setState({ showFormModal: true, isSubMode: false, isEditing: false, editingDepartment: null, departmentName: "", departmentDescription: "", selectedParentId: null, error: null })}
              className="px-4 py-2 bg-primary-blue text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              New Department
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-4 w-full">
          <div>
            <p className="text-sm text-gray-500">Create and manage departments and their sub-departments</p>
          </div>

          {/* Status Messages */}
          {success && (
            <div className="px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              {success}
            </div>
          )}
          {error && !showFormModal && !showDeleteModal && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Departments List */}
          <div className="w-full border border-gray-200 rounded-xl overflow-visible">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 rounded-t-xl">
              <p className="font-inter font-semibold text-sm text-gray-700 uppercase tracking-wider">
                Departments ({departments.length})
              </p>
            </div>
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_5rem] items-center px-6 py-2 border-b border-gray-100 bg-gray-50/50">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Department</p>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider text-center">Actions</p>
            </div>
            <div className="divide-y divide-gray-100 overflow-y-auto max-h-[calc(100vh-24rem)] overflow-x-visible">
              {isLoading ? (
                <div className="flex justify-center items-center py-10">
                  <Spinner />
                </div>
              ) : departments && departments.length > 0 ? (
                departments.map((department) => (
                  <React.Fragment key={department.id}>
                    {this.renderDepartmentItem(department)}
                  </React.Fragment>
                ))
              ) : (
                <div className="px-6 py-12 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-700 mb-1">No departments yet</p>
                  <p className="text-sm text-gray-400">Get started by creating a new department</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Floating action menu — rendered outside the table to avoid overflow clipping */}
        {openActionMenuId && menuDepartment && actionMenuPosition && (
          <div
            className="fixed z-50 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1"
            style={{ top: actionMenuPosition.top, right: actionMenuPosition.right }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => { this.setState({ openActionMenuId: null, actionMenuPosition: null }); this.handleEditClick(menuDepartment); }}
              className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
            {menuDepartmentIsTop && (
              <button
                onClick={() => { this.setState({ openActionMenuId: null, actionMenuPosition: null, showFormModal: true, isSubMode: true, isEditing: false, editingDepartment: null, departmentName: "", departmentDescription: "", selectedParentId: menuDepartment.id, parentPreselected: true, error: null }); }}
                className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Building2 className="w-3.5 h-3.5" />
                Add Sub-Department
              </button>
            )}
            <button
              onClick={() => { this.setState({ openActionMenuId: null, actionMenuPosition: null }); this.handleDeleteClick(menuDepartment); }}
              className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          </div>
        )}

        {/* Form Modal */}
        {showFormModal && this.renderFormModal()}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && deletingDepartment && (
          <Modal title={`Delete ${deletingDepartment.name}`} onClose={() => this.setState({ showDeleteModal: false, deletingDepartment: null, deleteConfirmationName: "", error: null })}>
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-500">
                This will permanently delete the department and its associated data. This action cannot be undone.
              </p>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm text-gray-600">
                  Type <span className="font-semibold text-gray-900">{deletingDepartment.name}</span> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmationName}
                  onChange={this.handleDeleteConfirmationNameChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-red-400 transition-colors"
                  placeholder="Enter department name"
                />
              </div>
              {error && (
                <p className="text-xs text-red-500">{error}</p>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => this.setState({ showDeleteModal: false, deletingDepartment: null, deleteConfirmationName: "", error: null })}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={this.handleConfirmDelete}
                  disabled={deleteConfirmationName !== deletingDepartment.name}
                  className="flex-1 px-4 py-2.5 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  departments: state.adminUser?.departments || [],
});

export default withRouterHooks(
  connect(mapStateToProps, {
    FetchAllDepartmentsAction,
    CreateDepartmentAction,
    UpdateDepartmentAction,
    DeleteDepartmentAction,
  })(CreateDepartments)
);
